import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';

import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { QueryTypes } from '../../../types/queryTypes';
import { MegaStoreReportItem } from '../../../types/reports/storeReportItem';
import { baseColumnDef, currencyColumnDef, statusColumnDef } from './baseColumnDef';

import { ReportDataGrid } from './reportDataGrid';
import { ReportDateSearch } from './reportDateSearch';

import { Button } from 'react-bootstrap';
import { ReportsConfig } from '../../../config/reportsConfig';
import { UtilService } from '../../../services';
import { ReportsService } from '../../../services/reports';
import { FlechaDer } from '../../svg';
import { MegaError } from '../megaError';
import { StoreReportItemDetails } from './storeReportItemDetails';

export const StoreReport: React.FC<{}> = ({ children }) => {
  const [reportItems, setReportItems] = useState<MegaStoreReportItem[]>([]);
  const [startDate, setStartDate] = useState<Date>(ReportsConfig.defaultStartDate);
  const [endDate, setEndDate] = useState<Date>(ReportsConfig.defaultEndDate);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [reportItemId, setReportItemId] = useState<number | undefined>();

  const storeReportQuery = useQuery([QueryTypes.GetStoreReport, startDate, endDate], () => {
    return ReportsService.getStoreReport({ startDate, endDate });
  });

  useEffect(() => {
    setIsSearching(storeReportQuery.isFetching);
  }, [storeReportQuery.isFetching]);

  useEffect(() => {
    if (storeReportQuery.status === 'success') {
      setReportItems(storeReportQuery.data);
    }
  }, [storeReportQuery.data]);

  const columns: GridColDef[] = [
    {
      ...baseColumnDef,
      field: 'created_date',
      headerName: 'Fecha',
      sortable: true,
      width: 180,
    },
    {
      ...statusColumnDef,
      field: 'order_status',
      headerName: 'Estado',
      width: 140,
    },
    {
      ...baseColumnDef,
      field: 'reference',
      headerName: 'Referencia',
      width: 140,
    },
    {
      ...baseColumnDef,
      field: 'payment_method',
      headerName: 'Método',
      flex: 1,
      minWidth: 140,
    },
    {
      ...currencyColumnDef,
      field: 'order_total',
      headerName: 'Total',
      width: 140,
    },
    {
      ...baseColumnDef,
      field: 'id',
      headerName: 'Acciones',
      renderCell: (params: GridRenderCellParams<number>) => (
        <strong>
          <Button
            variant="link"
            size="sm"
            tabIndex={params.hasFocus ? 0 : -1}
            className="text-secondary text-decoration-none"
            onClick={() => setReportItemId(params.value)}>
            Ver detalle
            <FlechaDer width={20} fill="#8ab934" className="ms-2" />
          </Button>
        </strong>
      ),
      width: 140,
    },
  ];

  const onSearch = (startDate: Date, endDate: Date) => {
    setStartDate(startDate);
    setEndDate(endDate);

    storeReportQuery.refetch({});
  };

  return (
    <>
      <div className="mt-5">
        <ReportDateSearch
          startDate={startDate}
          endDate={endDate}
          isSearching={isSearching}
          onSearch={onSearch}
        />
      </div>

      {children}

      <div className="mt-3 mb-5">
        {UtilService.isErrorQueries([storeReportQuery]) ? (
          <MegaError
            displayRetry={true}
            retry={() => {
              UtilService.refetchQueries([storeReportQuery]);
            }}
          />
        ) : (
          <ReportDataGrid<MegaStoreReportItem>
            columns={columns}
            rows={reportItems}
            isSearching={isSearching}
          />
        )}
      </div>

      <StoreReportItemDetails id={reportItemId} onHide={() => setReportItemId(undefined)} />
    </>
  );
};
